"use client";

import type { CSSProperties } from "react";
import Image from "next/image";
import { type LogoControllerSection } from "@/app/(dashboard)/dashboard/webmaster/logo-controller/logo-controller-defaults";
import { cn } from "@/lib/utils";
import { Container } from "../ui/Container";
import { Reveal } from "../ui/Reveal";

type LogoItem = LogoControllerSection["logos"][number];

function LogoCard({
  logo,
  cardWidth,
  cardHeight,
  cardRadius,
}: {
  logo: LogoItem;
  cardWidth: number;
  cardHeight: number;
  cardRadius: number;
}) {
  const card = (
    <div
      className={cn(
        "relative overflow-hidden border border-[#c8e0de] bg-white shadow-[0_4px_14px_rgba(0,80,80,0.06)] transition-colors duration-200 hover:border-2 hover:border-[#ffcc03]",
        // Fluid on small screens (fills its grid cell, keeps aspect ratio); configured size from md up.
        "w-full max-w-(--card-w) md:w-(--card-w)",
        logo.no_padding ? "p-0" : "p-[6px]",
      )}
      style={
        {
          "--card-w": `${cardWidth}px`,
          aspectRatio: `${cardWidth} / ${cardHeight}`,
          borderRadius: cardRadius,
        } as CSSProperties
      }
    >
      <Image
        src={logo.image_url}
        alt={logo.alt}
        quality={85}
        fill
        sizes="(max-width: 768px) 50vw, 240px"
        className="object-contain"
        style={{
          transform: logo.scale !== 1 ? `scale(${logo.scale})` : undefined,
        }}
      />
    </div>
  );

  if (!logo.link) return card;

  return (
    <a
      href={logo.link}
      target="_blank"
      rel="noopener noreferrer"
      className="block w-full"
    >
      {card}
    </a>
  );
}

const MEDIA_PARTNER_TARGET = "logo-dad51358-7d74-40c4-a74f-65b78bd8b91c.png";
const MEDIA_PARTNER_GROUP = "សហការផ្សព្វផ្សាយដោយ";

function moveTargetToIndex(
  items: LogoItem[],
  targetIndex: number,
  targetMatch: string,
): LogoItem[] {
  const index = items.findIndex((logo) => logo.image_url.includes(targetMatch));
  if (index < 0 || index === targetIndex || items.length <= targetIndex)
    return items;

  const next = [...items];
  const [target] = next.splice(index, 1);
  next.splice(targetIndex, 0, target);
  return next;
}

export function SponsorSection({
  logoSection,
}: {
  logoSection: LogoControllerSection;
}) {
  if (!logoSection.is_active || logoSection.logos.length === 0) return null;

  const groupOrder: string[] = [];
  const groupedLogos = new Map<string, LogoItem[]>();
  for (const logo of logoSection.logos) {
    const title =
      logo.group ||
      (logo.image_url.includes(MEDIA_PARTNER_TARGET)
        ? MEDIA_PARTNER_GROUP
        : "");
    if (!groupedLogos.has(title)) {
      groupOrder.push(title);
      groupedLogos.set(title, []);
    }
    groupedLogos.get(title)!.push(logo);
  }

  const { card_width, card_height, card_radius } = logoSection;

  return (
    <section className="py-16 md:py-24">
      <Container className="max-w-[1380px]">
        <div className="mx-auto max-w-[1328px]">
          {groupOrder.map((title, index) => {
            const items = groupedLogos.get(title)!;
            const displayItems = items.some((logo) =>
              logo.image_url.includes(MEDIA_PARTNER_TARGET),
            )
              ? moveTargetToIndex(items, 9, MEDIA_PARTNER_TARGET).slice(0, 10)
              : items;

            return (
              <Reveal key={title || `ungrouped-${index}`} delay={index * 0.04}>
                <div className={index === groupOrder.length - 1 ? "" : "mb-12"}>
                  {title && (
                    <div className="mb-12 text-center md:mb-14">
                      <div className="text-2xl font-bold tracking-[0.04em] text-[#d6efe8] md:text-[2rem]">
                        {title}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-center">
                    <div className="grid w-full grid-cols-3 place-items-center gap-x-3 gap-y-4 sm:grid-cols-4 md:w-auto md:grid-cols-5 md:gap-x-8 md:gap-y-10">
                      {displayItems.map((logo) => (
                        <div
                          key={logo.image_url}
                          className={cn(
                            "flex w-full justify-center",
                            logo.image_url.includes(MEDIA_PARTNER_TARGET) &&
                              "md:col-start-5 md:row-start-2",
                          )}
                        >
                          <LogoCard
                            logo={logo}
                            cardWidth={card_width}
                            cardHeight={card_height}
                            cardRadius={card_radius}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
}
